import React, { useState, useEffect } from 'react';
import { masterDataApi, API_BASE_URL } from '../../services/api';
import { handleApiError, showSuccess } from '../../utils/handleApiError';
import ConfirmModal from '../ConfirmModal';

interface Medicine {
  id: number;
  name: string;
  active_ingredient?: string;
  manufacturer?: string;
  default_withdrawal_days?: number;
  prescription_required?: boolean;
  unit_size?: number;
  unit_type?: string;
  description?: string;
}

const MedicineManagement: React.FC = () => {
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState<Medicine | null>(null);
  const [unitTypes, setUnitTypes] = useState<any[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [newMedicine, setNewMedicine] = useState({
    name: '',
    active_ingredient: '',
    manufacturer: '',
    default_withdrawal_days: '',
    prescription_required: false,
    unit_size: '',
    unit_type: '',
    description: ''
  });

  useEffect(() => {
    loadMedicines();
    loadUnitTypes();
  }, []);

  const loadMedicines = async () => {
    try {
      console.log('Loading medicines...');
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/medicines`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Medicines response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded medicines data:', data);
        setMedicines(data);
      } else {
        const errorData = await response.json();
        console.error('Error loading medicines:', errorData);
        handleApiError({ response: { data: errorData } }, 'Hiba történt a gyógyszerek betöltésekor');
      }
    } catch (error) {
      console.error('Error loading medicines:', error);
      handleApiError(error, 'Hiba történt a gyógyszerek betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const loadUnitTypes = async () => {
    try {
      const category = await masterDataApi.getCategory('medicine_quantity_units');
      setUnitTypes(category.values || []);
    } catch (error) {
      console.error('Error loading unit types:', error);
      handleApiError(error, 'Hiba történt az egység típusok betöltésekor');
    }
  };

  const handleAdd = async () => {
    try {
      const payload = {
        ...newMedicine,
        default_withdrawal_days: newMedicine.default_withdrawal_days ? parseInt(newMedicine.default_withdrawal_days) : null,
        unit_size: newMedicine.unit_size ? parseFloat(newMedicine.unit_size) : null
      };
      
      console.log('Adding medicine with payload:', payload);
      
      const response = await fetch(`${API_BASE_URL}/medicines`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Added medicine:', result);
        
        setShowAddModal(false);
        setNewMedicine({
          name: '',
          active_ingredient: '',
          manufacturer: '',
          default_withdrawal_days: '',
          prescription_required: false,
          unit_size: '',
          unit_type: '',
          description: ''
        });
        loadMedicines();
        showSuccess('Gyógyszer sikeresen hozzáadva!');
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        handleApiError({ response: { data: errorData } }, 'Hiba történt a gyógyszer hozzáadásakor');
      }
    } catch (error) {
      console.error('Error adding medicine:', error);
      handleApiError(error, 'Hiba történt a gyógyszer hozzáadásakor');
    }
  };

  const handleEdit = async () => {
    if (!editingMedicine) return;

    try {
      const response = await fetch(`${API_BASE_URL}/medicines/${editingMedicine.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingMedicine)
      });

      if (response.ok) {
        showSuccess('Gyógyszer sikeresen frissítve');
        setEditingMedicine(null);
        loadMedicines();
      }
    } catch (error) {
      console.error('Error updating medicine:', error);
      handleApiError(error, 'Hiba történt a gyógyszer frissítésekor');
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;

    try {
      const response = await fetch(`${API_BASE_URL}/medicines/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showSuccess('Gyógyszer sikeresen törölve');
        loadMedicines();
      }
    } catch (error) {
      console.error('Error deleting medicine:', error);
      handleApiError(error, 'Hiba történt a gyógyszer törlésekor');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gyógyszer kezelés</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          + Új gyógyszer
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Név
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hatóanyag
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Gyártó
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Karencia
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Receptköteles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Műveletek
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {medicines.map((medicine) => (
              <tr key={medicine.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {medicine.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {medicine.active_ingredient || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {medicine.manufacturer || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {medicine.default_withdrawal_days ? `${medicine.default_withdrawal_days} nap` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    medicine.prescription_required ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {medicine.prescription_required ? 'Igen' : 'Nem'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => setEditingMedicine(medicine)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Szerkesztés
                  </button>
                  <button
                    onClick={() => setDeleteId(medicine.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Törlés
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Új gyógyszer hozzáadása</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Név *</label>
                <input
                  type="text"
                  value={newMedicine.name}
                  onChange={(e) => setNewMedicine({...newMedicine, name: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Hatóanyag</label>
                <input
                  type="text"
                  value={newMedicine.active_ingredient}
                  onChange={(e) => setNewMedicine({...newMedicine, active_ingredient: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Gyártó</label>
                <input
                  type="text"
                  value={newMedicine.manufacturer}
                  onChange={(e) => setNewMedicine({...newMedicine, manufacturer: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Karencia (napok)</label>
                <input
                  type="number"
                  min="0"
                  value={newMedicine.default_withdrawal_days}
                  onChange={(e) => setNewMedicine({...newMedicine, default_withdrawal_days: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newMedicine.prescription_required}
                    onChange={(e) => setNewMedicine({...newMedicine, prescription_required: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Receptköteles</span>
                </label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Egységméret</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newMedicine.unit_size}
                    onChange={(e) => setNewMedicine({...newMedicine, unit_size: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Egység típus</label>
                  <select
                    value={newMedicine.unit_type}
                    onChange={(e) => setNewMedicine({...newMedicine, unit_type: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Válassz egységet...</option>
                    {unitTypes.map((type) => (
                      <option key={type.key} value={type.key}>{type.value}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Leírás</label>
                <textarea
                  value={newMedicine.description}
                  onChange={(e) => setNewMedicine({...newMedicine, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Mégse
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Hozzáadás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingMedicine && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Gyógyszer szerkesztése</h3>
              <button
                onClick={() => setEditingMedicine(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Név *</label>
                <input
                  type="text"
                  value={editingMedicine.name}
                  onChange={(e) => setEditingMedicine({...editingMedicine, name: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Hatóanyag</label>
                <input
                  type="text"
                  value={editingMedicine.active_ingredient || ''}
                  onChange={(e) => setEditingMedicine({...editingMedicine, active_ingredient: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Gyártó</label>
                <input
                  type="text"
                  value={editingMedicine.manufacturer || ''}
                  onChange={(e) => setEditingMedicine({...editingMedicine, manufacturer: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Karencia (napok)</label>
                <input
                  type="number"
                  min="0"
                  value={editingMedicine.default_withdrawal_days || ''}
                  onChange={(e) => setEditingMedicine({...editingMedicine, default_withdrawal_days: e.target.value ? parseInt(e.target.value) : undefined})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingMedicine.prescription_required || false}
                    onChange={(e) => setEditingMedicine({...editingMedicine, prescription_required: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Receptköteles</span>
                </label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Egységméret</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingMedicine.unit_size || ''}
                    onChange={(e) => setEditingMedicine({...editingMedicine, unit_size: e.target.value ? parseFloat(e.target.value) : undefined})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Egység típus</label>
                  <select
                    value={editingMedicine.unit_type || ''}
                    onChange={(e) => setEditingMedicine({...editingMedicine, unit_type: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Válassz egységet...</option>
                    {unitTypes.map((type) => (
                      <option key={type.key} value={type.key}>{type.value}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Leírás</label>
                <textarea
                  value={editingMedicine.description || ''}
                  onChange={(e) => setEditingMedicine({...editingMedicine, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setEditingMedicine(null)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Mégse
              </button>
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Mentés
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={deleteId !== null}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        title="Gyógyszer törlése"
        message="Biztosan törölni szeretnéd ezt a gyógyszert?"
        confirmLabel="Törlés"
      />
    </div>
  );
};

export default MedicineManagement;